import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BRAND, KIDS_TAGLINE } from "@/lib/site";
import { usePosts } from "@/hooks/usePosts";
import { useSeason } from "@/hooks/useSeason";
import { Heart, Star } from "@/components/Doodles";
import { SignupBox } from "@/components/site/SignupBox";
import { ShopGrid, useListings } from "@/components/site/ShopGrid";
import { HowItWorks, RightNow, SeasonHero, SectionHeading, SectionTiles } from "@/components/site/HomeBlocks";

function KidsFeature() {
  return (
    <section className="little-hands-theme mx-auto mt-24 max-w-7xl px-5 sm:px-8">
      <Link to="/gifts/kids" className="calm-card group relative grid items-center gap-8 overflow-hidden p-6 sm:p-10 lg:grid-cols-2 lg:gap-16" data-testid="home-kids-feature">
        <div className="relative">
          <Star className="ink-doodle absolute -top-2 right-2 h-9 w-9 rotate-12" />
          <p className="shop-eyebrow">Kid-made collection</p>
          <p className="shop-h1 mt-4 text-4xl sm:text-5xl">{KIDS_TAGLINE}</p>
          <p className="hand-note mt-3 -rotate-1 text-2xl sm:text-3xl">they make it, you help a little</p>
          <p className="mt-4 max-w-md text-base leading-relaxed text-[#78716C]">Fill-in cards, coupon books and handprint keepsakes kids make themselves.</p>
          <span className="btn-solid mt-7">Explore Little Hands <ArrowRight className="h-4 w-4" /></span>
        </div>
        <div className="relative">
          <img src="/images/hero-drawing.jpg" alt="A child drawing a handmade card" className="aspect-[4/3] w-full rounded-2xl object-cover" />
          <Heart className="ink-doodle absolute -bottom-4 -left-4 h-12 w-12 -rotate-12" />
        </div>
      </Link>
    </section>
  );
}

function ShopPicks() {
  const items = useListings();
  if (!items?.length) return null;
  return (
    <section className="mx-auto mt-24 max-w-7xl px-5 sm:px-8" data-testid="home-shop-picks">
      <SectionHeading eyebrow="Printable gift kits" title="Done-for-you printables" sub="Instant download, print at home in US Letter or A4, finish it by hand." to="/shop" label="Shop all printables" testId="home-shop-all" />
      <ShopGrid items={items.slice(0, 3)} />
    </section>
  );
}

function HelloKelly() {
  return (
    <section className="mx-auto mt-24 grid max-w-7xl items-center gap-8 px-5 sm:px-8 lg:grid-cols-[1fr_1.3fr] lg:gap-16" data-testid="home-hello">
      <div>
        <p className="shop-eyebrow">Hi, I'm Kelly</p>
        <p className="shop-h2 mt-3 text-2xl sm:text-3xl">The gifts people keep are rarely the expensive ones.</p>
        <p className="mt-4 text-base leading-relaxed text-[#78716C]">They're the ones somebody made. I share handmade gift ideas with the parts most posts skip: what to buy, how long it really takes, what it costs, and the steps in order.</p>
        <Link to="/about" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#1C1917] underline underline-offset-4" data-testid="home-about-link">More about me <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <SignupBox source="home" />
    </section>
  );
}

export default function HomePage() {
  const posts = usePosts();
  const season = useSeason();
  usePageMeta(`${BRAND} | Handmade gift ideas and printables`, "Easy step-by-step DIY gift ideas for everyone on your list, plus print-at-home gift kits. Gifts for her, him, teachers, holidays and kids to make.");
  return (
    <div data-testid="home-page">
      {season ? <SeasonHero season={season} /> : <div className="h-[520px]" />}
      <HowItWorks />
      {season && <RightNow season={season} />}
      <SectionTiles posts={posts} />
      <ShopPicks />
      <KidsFeature />
      <HelloKelly />
    </div>
  );
}
